import LegalShell from "@/components/LegalShell";

export default function Terms() {
    return (
        <LegalShell
            title="Terms of Service"
            subtitle="The rules of using Bosun's Mate. Plain English, no fluff."
            lastUpdated="June 2026"
        >
            <h2>1. Who we are</h2>
            <p>
                Bosun's Mate is operated by Bell Health Services and Technology
                ("we", "us") out of Whitehorse, Yukon, Canada. By using the app
                you agree to these terms.
            </p>

            <h2>2. Your account</h2>
            <ul>
                <li>You must be 18 or older to create an account.</li>
                <li>You're responsible for keeping your password secure.</li>
                <li>One person per account. Sharing logins isn't allowed.</li>
            </ul>

            <h2>3. Free plan and Pro trial</h2>
            <p>
                New accounts start with a 7-day free trial of Pro features — no credit
                card required. When the trial ends, your account moves to the Free
                plan (one vessel with unlimited log entries) with no time limit.
                Pro features (unlimited vessels, co-captains, AI extraction, charts,
                checklists and more) require a paid subscription. Your data is
                preserved regardless of plan.
            </p>

            <h2>4. Subscriptions and billing</h2>
            <ul>
                <li>Web and Android subscriptions are billed in advance via Stripe.</li>
                <li>iOS subscriptions are billed through your Apple&nbsp;ID via Apple In-App Purchase.</li>
                <li>Monthly and Annual plans auto-renew until you cancel.</li>
                <li>Lifetime plan is a one-time payment with no recurring charge — limited to 150 seats total.</li>
                <li>You can cancel any time from the Pricing page or the Stripe billing portal. No partial refunds for unused time in the current period.</li>
                <li><strong>14-day refund:</strong> If you're not satisfied within 14 days of your first payment, email <a className="bm-link" href="mailto:captain@bosunsmate.app">captain@bosunsmate.app</a> for a full refund.</li>
            </ul>

            <h2>4a. Native In-App Subscriptions (iOS and Android)</h2>
            <p>
                If you subscribe through the iOS or Android app, the following
                store-specific terms apply in addition to the rest of this
                agreement:
            </p>
            <ul>
                <li><strong>Auto-renewable subscriptions.</strong> Bosun's Mate Pro is offered as a 1-month or 1-year auto-renewing subscription. Payment will be charged to your Apple&nbsp;ID (iOS) or Google&nbsp;Play account (Android) at confirmation of purchase.</li>
                <li><strong>Renewal.</strong> Your subscription automatically renews for the same period at the then-current price unless auto-renew is turned off at least 24&nbsp;hours before the end of the current period. Your account will be charged for renewal within 24&nbsp;hours prior to the end of the current period.</li>
                <li>
                    <strong>Manage / cancel.</strong>{" "}
                    <em>iOS:</em> Apple&nbsp;ID Account&nbsp;Settings &rsaquo; Subscriptions.{" "}
                    <em>Android:</em> Play&nbsp;Store &rsaquo; Profile &rsaquo; Payments &amp; subscriptions &rsaquo; Subscriptions.
                </li>
                <li><strong>Free trial.</strong> Any unused portion of a free trial period (where offered) will be forfeited when you purchase a subscription.</li>
                <li>
                    <strong>Refunds.</strong>{" "}
                    <em>iOS:</em> handled by Apple at{" "}
                    <a className="bm-link" href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">reportaproblem.apple.com</a>.{" "}
                    <em>Android:</em> handled by Google at{" "}
                    <a className="bm-link" href="https://play.google.com/store/account/orderhistory" target="_blank" rel="noopener noreferrer">play.google.com/store/account/orderhistory</a>.
                </li>
                <li>
                    By subscribing via iOS you also accept the{" "}
                    <a
                        className="bm-link"
                        href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Apple Standard End User License Agreement (EULA)
                    </a>. By subscribing via Android you also accept the{" "}
                    <a
                        className="bm-link"
                        href="https://play.google.com/about/play-terms/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Google Play Terms of Service
                    </a>.
                </li>
            </ul>

            <h2>5. Acceptable use</h2>
            <p>You agree not to:</p>
            <ul>
                <li>Reverse-engineer, scrape, or attempt to access another user's data.</li>
                <li>Use the service to store unlawful content.</li>
                <li>Resell, sublicense, or white-label the app without our written agreement.</li>
            </ul>

            <h2>6. Your data</h2>
            <p>
                You own your data. We just store it for you. You can export it any
                time using the PDF export feature. See our <a className="bm-link" href="/privacy">Privacy Policy</a> for full details.
            </p>

            <h2>7. Service availability</h2>
            <p>
                We aim for 99.5% uptime but don't guarantee uninterrupted service.
                We do daily backups. If something breaks, we'll fix it as fast as we
                can.
            </p>

            <h2>8. Disclaimer</h2>
            <p>
                Bosun's Mate is a record-keeping tool. It is <strong>not</strong> a
                substitute for marine survey, professional maintenance advice, or
                insurance documentation. We're not liable for decisions you make
                based on data you've logged.
            </p>

            <h2>9. Termination</h2>
            <p>
                You can delete your account anytime (email us). We can suspend
                accounts that violate these terms, with a 14-day notice except in
                cases of fraud or abuse.
            </p>

            <h2>10. Changes to these terms</h2>
            <p>
                If we make material changes, we'll email you at least 30 days
                before the new terms take effect.
            </p>

            <h2>11. Governing law</h2>
            <p>
                These terms are governed by the laws of Yukon, Canada. Any disputes
                go to the courts of Yukon.
            </p>

            <h2>12. Contact</h2>
            <p>
                Email <a className="bm-link" href="mailto:captain@bosunsmate.app">captain@bosunsmate.app</a>.
            </p>
        </LegalShell>
    );
}
